/**
 * Competition Zone Overview Page
 * Main dashboard for the Competition Zone module
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Trophy, Plus, Swords, Users, Target, Calendar, Clock,
  TrendingUp, Award, Crown, Medal, Star, ChevronRight,
  Filter, Search, BarChart3, Zap, CheckCircle2, Rocket
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge configurations
const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  ACTIVE: { label: 'Active', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  COMPLETED: { label: 'Completed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  CANCELLED: { label: 'Cancelled', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
};

// Category badge configurations
const CATEGORY_CONFIG = {
  SALES: { label: 'Sales', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: TrendingUp },
  OPS: { label: 'Operations', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: Zap },
  PRODUCTION: { label: 'Production', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400', icon: Target },
  CREWS: { label: 'Crews', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Users },
  MARKETING: { label: 'Marketing', className: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400', icon: Star },
  LEADERSHIP: { label: 'Leadership', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400', icon: Crown },
  GENERAL: { label: 'General', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', icon: Trophy }
};

// Metric type formatters
const formatMetricValue = (value, metricType) => {
  switch (metricType) {
    case 'CURRENCY':
      return `$${value?.toLocaleString() || 0}`;
    case 'PERCENT':
      return `${value || 0}%`;
    case 'HOURS':
      return `${value || 0}h`;
    case 'POINTS':
      return `${value || 0} pts`;
    default:
      return value?.toLocaleString() || 0;
  }
};

// Competition Card Component
const CompetitionCard = ({ competition, onView, onJoin }) => {
  const config = STATUS_CONFIG[competition.status] || STATUS_CONFIG.DRAFT;
  const categoryConfig = CATEGORY_CONFIG[competition.category] || CATEGORY_CONFIG.GENERAL;
  const CategoryIcon = categoryConfig.icon;
  
  const isActive = competition.status === 'ACTIVE';
  const canJoin = isActive && competition.allow_open_join && !competition.my_status;
  const isInvited = competition.my_status === 'INVITED';
  
  const timeRemaining = competition.end_at 
    ? Math.max(0, Math.ceil((new Date(competition.end_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer border-l-4"
      style={{ borderLeftColor: isActive ? '#22c55e' : '#94a3b8' }}
      onClick={() => onView(competition)}
      data-testid={`competition-card-${competition.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={categoryConfig.className}>
                <CategoryIcon className="h-3 w-3 mr-1" />
                {categoryConfig.label}
              </Badge>
              <Badge className={config.className}>{config.label}</Badge>
              {competition.is_challenge && (
                <Badge variant="outline" className="text-xs">
                  <Swords className="h-3 w-3 mr-1" />
                  Challenge
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-lg truncate">{competition.title}</h3>
            {competition.description && (
              <p className="text-sm text-muted-foreground line-clamp-1">{competition.description}</p>
            )}
          </div>
          {competition.my_rank && (
            <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <Medal className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                #{competition.my_rank}
              </span>
            </div>
          )}
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            {competition.participant_count} participants
          </div>
          <div className="flex items-center gap-1">
            <Target className="h-4 w-4" />
            Goal: {formatMetricValue(competition.target_value, competition.metric_type)}
          </div>
          {timeRemaining !== null && isActive && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {timeRemaining}d left
            </div>
          )}
        </div>

        {/* Progress Bar (for user's own progress) */}
        {competition.my_score !== undefined && competition.my_score !== null && (
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">Your Progress</span>
              <span className="font-medium">
                {formatMetricValue(competition.my_score, competition.metric_type)} / {formatMetricValue(competition.target_value, competition.metric_type)}
              </span>
            </div>
            <Progress 
              value={Math.min((competition.my_score / competition.target_value) * 100, 100)} 
              className="h-2"
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              onView(competition);
            }}
          >
            View Details
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          {canJoin && (
            <Button 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                onJoin(competition);
              }}
            >
              Join
            </Button>
          )}
          {isInvited && (
            <div className="flex gap-1">
              <Button 
                size="sm" 
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onJoin(competition, 'DECLINED');
                }}
              >
                Decline
              </Button>
              <Button 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onJoin(competition, 'ACCEPTED');
                }}
              >
                Accept
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Winner Announcement Card
const WinnerCard = ({ entry }) => {
  return (
    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className="h-12 w-12 border-2 border-amber-400">
              <AvatarFallback className="bg-amber-100 text-amber-700">
                {entry.winner_name?.charAt(0) || '?'}
              </AvatarFallback>
            </Avatar>
            <Crown className="absolute -top-1 -right-1 h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{entry.winner_name}</p>
            <p className="text-sm text-muted-foreground truncate">{entry.competition_title}</p>
          </div>
          <Badge className={CATEGORY_CONFIG[entry.category]?.className}>
            {CATEGORY_CONFIG[entry.category]?.label || entry.category}
          </Badge>
        </div>
        {entry.props_message && (
          <p className="mt-2 text-sm italic text-muted-foreground">"{entry.props_message}"</p>
        )}
      </CardContent>
    </Card>
  );
};

// Quick Stats Component
const QuickStats = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.active || 0}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Trophy className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.myWins || 0}</p>
            <p className="text-xs text-muted-foreground">My Wins</p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Medal className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.participating || 0}</p>
            <p className="text-xs text-muted-foreground">Participating</p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Star className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.streak || 0}</p>
            <p className="text-xs text-muted-foreground">Win Streak</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Overview Component
export const CompetitionOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [myCompetitions, setMyCompetitions] = useState([]);
  const [recentWinners, setRecentWinners] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const isLeadership = ['super_admin', 'admin', 'leadership', 'general_manager', 'ceo', 'president', 'director_of_sales'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch competitions
      const [allResponse, myResponse, winnersResponse] = await Promise.all([
        axios.get(`${API}/competitions?user_id=${user?.id}&status=ACTIVE`),
        axios.get(`${API}/competitions?user_id=${user?.id}&mine=true`),
        axios.get(`${API}/competitions/hall-of-fame?limit=5`)
      ]);
      
      setCompetitions(allResponse.data || []);
      setMyCompetitions(myResponse.data || []);
      setRecentWinners(winnersResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [fetchData, user?.id]);

  const handleViewCompetition = (competition) => {
    navigate(`/competition/competitions/${competition.id}`);
  };

  const handleJoinCompetition = async (competition, status = 'ACCEPTED') => {
    try {
      if (competition.my_status === 'INVITED') {
        // Update status
        const participant = await axios.get(`${API}/competitions/${competition.id}/participants`);
        const myParticipant = participant.data?.find(p => p.entity_id === user?.id);
        if (myParticipant) {
          await axios.put(
            `${API}/competitions/${competition.id}/participants/${myParticipant.id}/status?user_id=${user?.id}`,
            { status }
          );
        }
      } else {
        // Join competition
        await axios.post(`${API}/competitions/${competition.id}/join?user_id=${user?.id}`);
      }
      
      toast.success(status === 'ACCEPTED' ? 'Joined competition!' : 'Invitation declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join competition');
    }
  };

  // Filter competitions
  const filteredCompetitions = competitions.filter(comp => {
    if (searchQuery && !comp.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (categoryFilter !== 'all' && comp.category !== categoryFilter) {
      return false;
    }
    return true;
  });

  const activeCompetitions = filteredCompetitions.filter(c => c.status === 'ACTIVE');
  const upcomingCompetitions = filteredCompetitions.filter(c => c.status === 'DRAFT');
  const completedCompetitions = filteredCompetitions.filter(c => c.status === 'COMPLETED');

  // Calculate stats
  const stats = {
    active: myCompetitions.filter(c => c.status === 'ACTIVE').length,
    myWins: recentWinners.filter(w => w.winner_entity_id === user?.id).length,
    participating: myCompetitions.length,
    streak: 0 // TODO: Calculate from API
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="competition-overview">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Trophy className="h-8 w-8 text-amber-500" />
              Competition Zone
            </h1>
            <p className="text-muted-foreground mt-1">
              Compete, track progress, and celebrate wins
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/competition/challenges')}
            >
              <Rocket className="h-4 w-4 mr-2" />
              Rhino-1 Challenges
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/competition/leaderboards')}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Leaderboards
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/competition/competitions/create?type=challenge')}
            >
              <Swords className="h-4 w-4 mr-2" />
              Challenge Someone
            </Button>
            {isLeadership && (
              <Button onClick={() => navigate('/competition/competitions/create')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Competition
              </Button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <QuickStats stats={stats} />

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search competitions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Competitions List */}
          <div className="lg:col-span-2 space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="active" className="gap-2">
                  <Zap className="h-4 w-4" />
                  Active ({activeCompetitions.length})
                </TabsTrigger>
                <TabsTrigger value="my" className="gap-2">
                  <Medal className="h-4 w-4" />
                  My Competitions ({myCompetitions.length})
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="gap-2">
                  <Calendar className="h-4 w-4" />
                  Upcoming ({upcomingCompetitions.length})
                </TabsTrigger>
                <TabsTrigger value="completed" className="gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Completed ({completedCompetitions.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="space-y-4 mt-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : activeCompetitions.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="font-semibold mb-2">No active competitions</h3>
                      <p className="text-muted-foreground mb-4">
                        {isLeadership 
                          ? 'Create a new competition to get started!'
                          : 'Check back soon for new competitions.'}
                      </p>
                      {isLeadership && (
                        <Button onClick={() => navigate('/competition/competitions/create')}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Competition
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  activeCompetitions.map(comp => (
                    <CompetitionCard
                      key={comp.id}
                      competition={comp}
                      onView={handleViewCompetition}
                      onJoin={handleJoinCompetition}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="my" className="space-y-4 mt-4">
                {myCompetitions.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Medal className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="font-semibold mb-2">You're not in any competitions yet</h3>
                      <p className="text-muted-foreground">
                        Join an active competition or challenge someone!
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  myCompetitions.map(comp => (
                    <CompetitionCard
                      key={comp.id}
                      competition={comp}
                      onView={handleViewCompetition}
                      onJoin={handleJoinCompetition}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="upcoming" className="space-y-4 mt-4">
                {upcomingCompetitions.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="font-semibold mb-2">No upcoming competitions</h3>
                      <p className="text-muted-foreground">
                        All competitions are currently active or completed.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  upcomingCompetitions.map(comp => (
                    <CompetitionCard
                      key={comp.id}
                      competition={comp}
                      onView={handleViewCompetition}
                      onJoin={handleJoinCompetition}
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="completed" className="space-y-4 mt-4">
                {completedCompetitions.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <h3 className="font-semibold mb-2">No completed competitions yet</h3>
                      <p className="text-muted-foreground">
                        Completed competitions will appear here.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  completedCompetitions.map(comp => (
                    <CompetitionCard
                      key={comp.id}
                      competition={comp}
                      onView={handleViewCompetition}
                      onJoin={handleJoinCompetition}
                    />
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Recent Winners */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-500" />
                  Recent Winners
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recentWinners.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No winners yet. Be the first!
                  </p>
                ) : (
                  recentWinners.slice(0, 5).map(entry => (
                    <WinnerCard key={entry.id} entry={entry} />
                  ))
                )}
                {recentWinners.length > 0 && (
                  <Button 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => navigate('/competition/leaderboards')}
                  >
                    View All
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/competition/challenges')}
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Rhino-1 Challenges
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/competition/leaderboards')}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Leaderboards
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/competition/competitions/create?type=challenge')}
                >
                  <Swords className="h-4 w-4 mr-2" />
                  Challenge a Colleague
                </Button>
                {isLeadership && (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate('/competition/templates')}
                    >
                      <Target className="h-4 w-4 mr-2" />
                      Competition Templates
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate('/competition/admin')}
                    >
                      <Trophy className="h-4 w-4 mr-2" />
                      Admin Settings
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CompetitionOverview;
